import { MutableRefObject, useEffect, useRef, useState } from 'react'
import { ReactState, ReactStatePromise, StateSetterPromise } from '../Types/Types'

type PromiseResolve<T> = (resolve: T | PromiseLike<T>) => void

/**
 * This is a useState hook that has a setter which returns a promise that resolves when the state has been set.
 *
 * @param initialValue - The initial variable to set the state to.
 */
export const useStateWithPromise = <T extends unknown>(initialValue: T): ReactStatePromise<T> => {
    const [state, setState]: ReactState<T> = useState<T>(initialValue)
    const resolverRef: MutableRefObject<Nullable<PromiseResolve<T>>> = useRef(null)

    useEffect((): void => {
        if (resolverRef.current) {
            resolverRef.current?.(state)
            resolverRef.current = null
        }
    }, [state])

    const setStateAsync: StateSetterPromise<T> = (value: T | ((value: T) => T)): Promise<T> =>
        new Promise((resolve: PromiseResolve<T>): void => {
            resolverRef.current = resolve
            setState(value)
        })

    return [state, setStateAsync]
}
